import {
  AMC,
  COMPANY,
  COMPANY_MEMBER,
  MOBILE,
  SOLE_PROPRIETOR,
} from '@components/Signup/constants'
import missingCompanyInfo from '../../helpers/missingCompanyInfo'

export const selectCurrentUserErrorMessage = state => state.currentUser.errorMessage

export const selectIsFetchingCurrentUser = state => state.currentUser.isFetching

export const selectIsFetchingCompanyAppraisers = state => state.currentUser.isFetchingCompanyAppraisers

export const selectIsUpdatingCompany = state => state.currentUser.isUpdatingCompany

export const selectIsUpdating = state => state.currentUser.isUpdating

export const selectIsChangingPassword = state => state.currentUser.isChanging

export const selectCurrentUserStatus = state => state.currentUser.status

export const selectInvitationStatus = state => state.currentUser.invitationStatus

export const selectCurrentUser = state => state.currentUser.currentUser

export const selectNotifications = state => state.currentUser.notifications

export const selectEOStatus = state => state.currentUser.eoStatus

export const selectAppraiserUpdateStatus = state => state.currentUser.appraiserUpdateStatus

export const selectRegistrationComplete = state => state.currentUser.registrationComplete

export const selectIsUpdatingCompanyUser = state => state.currentUser.isUpdatingCompanyUser

export const selectIsRemovingUserFromCompany = state => state.currentUser.isRemovingUserFromCompany

export const selectIsCreatingInvitation = state => state.currentUser.isCreatingInvitation

export const selectMessageNotifications = state => {
  return selectNotifications(state).filter(
    (notification) =>
      notification.notification_type === 'order_conversation_message',
  )
}

export const selectUnreadMessageNotifcationsCount = (state) => {
  return selectMessageNotifications(state).filter(
    (messageNotification) =>
      !messageNotification.viewed || messageNotification.viewed === 'None',
  ).length
}

export const selectRegularNotifications = state => {
  return selectNotifications(state).filter(
    (notification) =>
      notification.notification_type !== 'order_conversation_message',
  )
}

export const selectUnreadRegularNotifcationsCount = (state) => {
  return selectRegularNotifications(state).filter(
    (regularNotification) =>
      !regularNotification.viewed || regularNotification.viewed === 'None',
  ).length
}

export const selectCurrentUserId = state => {
  const { id } = selectCurrentUser(state) || {}
  return id
}

export const selectCurrentUserEmail = state => {
  const { email } = selectCurrentUser(state) || {}
  return email
}

export const selectCurrentUserName = state => {
  const { firstname, lastname } = selectCurrentUser(state) || {}
  return { firstname, lastname }
}

export const selectCurrentUserType = state => {
  const { user_type } = selectCurrentUser(state) || {}
  return user_type
}

export const selectCurrentUserCompany = state => {
  const { company } = selectCurrentUser(state) || {}
  return company
}

export const selectCurrentUserCompanyId = state => {
  const { id } = selectCurrentUserCompany(state) || {}
  return id
}

export const selectIsAmc = state => {
  const { is_amc } = selectCurrentUserCompany(state) || {}
  return is_amc
}

export const selectCurrentUserLicenses = state => {
  const { license: licenses = [] } = selectCurrentUser(state) || {}
  return licenses
}

export const selectCompanyCoverage = state => {
  const { company_coverage } = selectCurrentUserCompany(state) || {}
  return company_coverage
}

export const selectCompanyPaymentMethods = state => {
  const { payment_methods } = selectCurrentUserCompany(state) || {}
  return payment_methods || []
}

export const selectHasPaymentMethod = state => selectCompanyPaymentMethods(state).length > 0

export const selectCompanyDefaultPaymentMethod = (state) => {
  const defaultPaymentMethod =
    selectCompanyPaymentMethods(state).find(
      (paymentMethod) => paymentMethod.default_payment,
    ) || null
  return defaultPaymentMethod
}

export const selectShowDashboardNotification = state => {
  const { show_dashboard_notification } = selectCurrentUser(state) || {}
  return show_dashboard_notification
}

export const selectCurrentUserCompanyMembers = state => {
  const { appraisers = [], managers = [] } = selectCurrentUserCompany(state) || {}
  return { appraisers, managers }
}

export const selectAllCurrentUserCompanyMembers = state => {
  return { state }
}

export const selectPayoutMethod = state => {
  const { payout_method } = selectCurrentUserCompany(state) || {}
  return payout_method
}

export const selectStripeAccountType = state => {
  const { stripe_account_type } = selectCurrentUserCompany(state) || null
  return stripe_account_type
}

export const selectStripeDashboardLink = state => {
  const { stripe_dashboard_link } = selectCurrentUserCompany(state) || null
  return stripe_dashboard_link
}

export const selectHasPayoutMethod = state => !!selectPayoutMethod(state)

export const selectEOInformation = state => {
  const { eo_information } = selectCurrentUserCompany(state) || {}
  const {
    aggregate_limit,
    claim_limit,
    expiration_date,
    insurance_company,
    prior_acts,
  } = eo_information || {}
  return {
    aggregate_limit,
    claim_limit,
    expiration_date,
    insurance_company,
    prior_acts,
  }
}

export const selectEOFileUrl = isCompany => state => {
  if (isCompany) {
    const { eo } = selectCurrentUserCompany(state) || {}
    return eo
  } else {
    const { eo_url } = selectCurrentUser(state) || {}
    return eo_url
  }
}

export const selectTaxInformation = state => {
  const {
    tax_classification,
    llc_classification,
    other_classification,
  } = selectCurrentUserCompany(state) || {}
  return {
    tax_classification,
    llc_classification,
    other_classification,
  }
}

export const selectUserAddresses = state => {
  const currentUser = selectCurrentUser(state) || {}
  const {
    primary_address: primaryAddress,
  } = currentUser
  return { primaryAddress }
}

export const selectUserPhone = state => {
  const currentUser = selectCurrentUser(state) || {}
  const { phone, phone_type, cellphone } = currentUser
  if (!phone) {
    return {
      phone: cellphone,
      phoneType: MOBILE,
    }
  } else {
    return {
      phone,
      phoneType: phone_type,
    }
  }
}

export const selectAddressTypeForAllocation = state => {
  const currentUser = selectCurrentUser(state) || {}
  return currentUser.address_type_for_allocation
}

export const selectAllocationAddress = (state, addressForAllocation) => {
  const { primaryAddress, licenseAddress, companyAddress } = selectAddresses(state)
  if (addressForAllocation === 'primary') {
    return primaryAddress
  } else if (addressForAllocation === 'company') {
    return companyAddress
  }
  return licenseAddress
}

export const selectAddresses = state => {
  // Select either company address or users addresses based on user_type
  const currentUser = selectCurrentUser(state) || {}
  const {
    primary_address: primaryAddress,
  } = currentUser
  const firstLicense = currentUser.license && currentUser.license[0]
  let licenseAddress = null
  if (firstLicense) {
    licenseAddress = {
      address: firstLicense.company_address,
      city: firstLicense.company_city,
      state: firstLicense.company_state,
      zip: firstLicense.company_zip,
    }
  }
  if (currentUser.user_type !== COMPANY_MEMBER) {
    const { company } = currentUser
    const { address, city, state: stateCode, zip } = company || {}
    return {
      primaryAddress,
      licenseAddress,
      companyAddress: {
        address,
        city,
        state: stateCode,
        zip,
      },
    }
  }
  return { primaryAddress, licenseAddress }
}

export const selectCoverageData = state => {
  const { user_type, coverage, coverage_level } = selectCurrentUser(state) || {}
  const companyCoverage = selectCompanyCoverage(state)
  if (user_type !== COMPANY_MEMBER) {
    return { coverage: companyCoverage }
  } else {
    return {
      coverage,
      coverageLevel: coverage_level,
      companyCoverage,
    }
  }
}

export const selectCurrentUserCoverageData = state => {
  const { coverage, coverage_level } = selectCurrentUser(state) || {}
  return { coverage, coverage_level }
}

export const selectPhoneInfo = state => {
  const {
    user_type,
    phone: userPhone,
    phone_type: userPhoneType,
  } = selectCurrentUser(state) || {}
  if (user_type === COMPANY || user_type === SOLE_PROPRIETOR || user_type === AMC) {
    const {
      phone: companyPhone,
      phone_type: companyPhoneType,
    } = selectCurrentUserCompany(state)
    return {
      phone: companyPhone,
      phoneType: companyPhoneType,
    }
  } else {
    return {
      phone: userPhone,
      phoneType: userPhoneType,
    }
  }
}

export const selectIsMissingCompanyInfo = state => missingCompanyInfo(selectCurrentUser(state))

export const selectCompanyAcceptingJobs = state => {
  const { accepting_jobs } = selectCurrentUserCompany(state) || {}
  return accepting_jobs
}

export const selectAvailableRoles = state => {
  const { available_roles } = selectCurrentUser(state) || {}
  return available_roles
}

export const selectCurrentUserIsManagerOrAdmin = state => {
  const { role: userRole, available_roles: userRoles } = selectCurrentUser(state) || {}
  if (userRole === 'manager') {
    return true
  }

  if (!userRole && !userRoles) {
    return false
  }

  return userRoles.includes('admin') || userRoles.includes('manager')
}

export const selectCompanyName = state => {
  const { name } = selectCurrentUserCompany(state) || {}
  return name
}
